import { createTheme } from '@mui/material/styles';

// ---------------------------------------------------------------------------
// Design tokens — dark & sophisticated.
// One restrained accent. To re-skin the whole site, change ACCENT below.
// (Try cyan '#6ee7ff' for a cooler feel.)
// ---------------------------------------------------------------------------
const ACCENT = '#c6f24e'; // electric lime
const ACCENT_DARK = '#a6d62f';

export const tokens = {
  accent: ACCENT,
  bg: '#0a0a0b',
  surface: '#141416',
  surfaceHover: '#1b1b1e',
  textPrimary: '#f4f4f5',
  textSecondary: '#a1a1aa',
  textFaint: '#71717a',
  hairline: 'rgba(255, 255, 255, 0.08)',
  hairlineStrong: 'rgba(255, 255, 255, 0.16)',
  fontDisplay: '"Space Grotesk", "Inter", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, -apple-system, sans-serif',
  fontMono: '"JetBrains Mono", "SF Mono", Menlo, monospace',
};

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: ACCENT,
      light: '#d4f775',
      dark: ACCENT_DARK,
      contrastText: '#0a0a0b',
    },
    secondary: {
      main: '#a1a1aa',
      light: '#d4d4d8',
      dark: '#52525b',
    },
    background: {
      default: tokens.bg,
      paper: tokens.surface,
    },
    text: {
      primary: tokens.textPrimary,
      secondary: tokens.textSecondary,
    },
    divider: tokens.hairline,
    info: { main: '#6ee7ff', light: '#a5f0ff', dark: '#22d3ee' },
    success: { main: '#86efac', light: '#bbf7d0', dark: '#4ade80' },
    warning: { main: '#fcd34d', light: '#fde68a', dark: '#fbbf24' },
  },
  typography: {
    fontFamily: tokens.fontBody,
    h1: {
      fontFamily: tokens.fontDisplay,
      fontSize: 'clamp(2.75rem, 7vw, 5rem)',
      fontWeight: 600,
      lineHeight: 1.04,
      letterSpacing: '-0.03em',
    },
    h2: {
      fontFamily: tokens.fontDisplay,
      fontSize: 'clamp(2rem, 4.5vw, 3rem)',
      fontWeight: 600,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: tokens.fontDisplay,
      fontSize: 'clamp(1.5rem, 3vw, 2rem)',
      fontWeight: 500,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: tokens.fontDisplay,
      fontSize: '1.35rem',
      fontWeight: 500,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontFamily: tokens.fontMono,
      fontSize: '0.875rem',
      fontWeight: 500,
      letterSpacing: '0.02em',
    },
    body1: {
      fontSize: '1.0625rem',
      lineHeight: 1.7,
    },
    body2: {
      fontSize: '0.9375rem',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: tokens.fontMono,
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.bg,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          letterSpacing: '0.01em',
          borderRadius: 6,
          padding: '12px 26px',
          fontSize: '0.9rem',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        contained: {
          backgroundColor: ACCENT,
          color: '#0a0a0b',
          '&:hover': {
            backgroundColor: ACCENT_DARK,
            boxShadow: `0 8px 30px ${ACCENT}33`,
          },
        },
        outlined: {
          borderColor: tokens.hairlineStrong,
          color: tokens.textPrimary,
          '&:hover': {
            borderColor: ACCENT,
            backgroundColor: `${ACCENT}0d`,
            color: ACCENT,
          },
        },
        text: {
          color: tokens.textSecondary,
          '&:hover': {
            backgroundColor: 'transparent',
            color: tokens.textPrimary,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.surface,
          backgroundImage: 'none',
          borderRadius: 12,
          boxShadow: 'none',
          border: `1px solid ${tokens.hairline}`,
          transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.35s ease, box-shadow 0.35s ease',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: tokens.fontMono,
          fontSize: '0.72rem',
          letterSpacing: '0.01em',
          borderRadius: 6,
        },
        outlined: {
          borderColor: tokens.hairline,
          color: tokens.textSecondary,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& label': { color: tokens.textFaint },
          '& label.Mui-focused': { color: ACCENT },
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: 'rgba(255,255,255,0.02)',
            color: tokens.textPrimary,
            '& fieldset': { borderColor: tokens.hairline },
            '&:hover fieldset': { borderColor: tokens.hairlineStrong },
            '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: '1px' },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          backgroundImage: 'none',
          boxShadow: 'none',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { backgroundColor: tokens.hairline },
        bar: { backgroundColor: ACCENT },
      },
    },
  },
});
